// Bearer-token routes: invite links (/i/), family photo and document links (/c/), and shared Node profiles (/n/).
const PRIVATE_PATH_PATTERN = /^\/(i|c|n)\/[^/]+/;
// The requester status link keeps its public short code and hides only the token.
const COLLECTION_STATUS_PATH_PATTERN = /^(\/collect\/[^/]+\/r\/)[^/]+/;
const PLACEHOLDER_ORIGIN = "https://analytics.invalid";

/**
 * Pages reached through a bearer token in their path. Analytics must receive a redacted path without the token, and
 * pixel page views that read the live location must be skipped.
 */
export const redactAnalyticsPageUrl = (pageUrl: string): { url: string; redacted: boolean } => {
  const url = new URL(pageUrl, PLACEHOLDER_ORIGIN);

  if (COLLECTION_STATUS_PATH_PATTERN.test(url.pathname)) {
    return { url: url.pathname.replace(COLLECTION_STATUS_PATH_PATTERN, "$1redacted"), redacted: true };
  }

  if (!PRIVATE_PATH_PATTERN.test(url.pathname)) {
    return { url: pageUrl, redacted: false };
  }

  return { url: url.pathname.replace(PRIVATE_PATH_PATTERN, "/$1/redacted"), redacted: true };
};

const TOKEN_URL_PATTERN = /(^|\/\/[^/\s?#]+)\/(i|c|n)\/[A-Za-z0-9-]{16,64}(?=[/?#\s]|$)/g;
// App links (awraq://invite/{token}), requester status links, and URL-encoded copies inside referrer or store query strings.
const LINK_TOKEN_PATTERNS = [
  /(invite\/)[A-Za-z0-9]{16,64}/gi,
  /(\/collect\/[^/\s?#]+\/r\/)[A-Za-z0-9]{1,64}/g,
  /(%2F(?:i|c|n)%2F|%2Finvite%2F)[A-Za-z0-9-]{16,64}/gi,
];

/**
 * Analytics SDKs copy the live location into many properties, such as the current URL, the referrer, and the session
 * entry URL, and persist some of them. Replaces every link token found in string values, at any depth.
 */
export const redactAnalyticsValue = <T>(value: T): T => {
  if (typeof value === "string") {
    return LINK_TOKEN_PATTERNS.reduce(
      (redacted, pattern) => redacted.replace(pattern, "$1redacted"),
      value.replace(TOKEN_URL_PATTERN, "$1/$2/redacted"),
    ) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactAnalyticsValue(item)) as T;
  }

  if (value && typeof value === "object" && !(value instanceof Date)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactAnalyticsValue(item)])) as T;
  }

  return value;
};

/** Token pages hold the token in the URL and in app-banner markup, so session replay must not record them. */
export const isPrivateTokenPath = (pathname: string): boolean =>
  PRIVATE_PATH_PATTERN.test(pathname) || COLLECTION_STATUS_PATH_PATTERN.test(pathname);
